import {
  type ComponentPropsWithoutRef,
  type ReactNode,
  forwardRef,
} from "react"
import { FeaturedIcon } from "./featuredIcon"
import { type OutlineIcon, type Logo } from "./iconCore"

export const paddingOptions = ["sm", "md", "lg"] as const

export type PaddingOptions = (typeof paddingOptions)[number]

export type CardProps = ComponentPropsWithoutRef<"div"> & {
  /**
   * Indicates how much padding there is around each `CardContent` section
   *
   * @default md
   */
  "data-padding"?: PaddingOptions
}

export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ className, ...props }, ref) => {
    return <div ref={ref} className={`card ${className ?? ""}`} {...props} />
  },
)

Card.displayName = "Card"

export type CardContentProps = ComponentPropsWithoutRef<"div"> & {
  /**
   * Indicates whether to display a top border between <CardContent /> components.
   * By default, a CardContent that follows immediately another CardContent will
   * have a thin top border to act as a visual separator. To disable this, set
   * data-border={false} on the second CardContent component
   *
   * @example
   *
   * <Card>
   *   <CardContent></CardContent>
   *   <CardContent data-border={false}></CardContent>
   * </Card>
   */
  "data-border"?: boolean
}

export const CardContent = forwardRef<HTMLDivElement, CardContentProps>(
  ({ className, ...props }, ref) => {
    return (
      <div ref={ref} className={`card-content ${className ?? ""}`} {...props} />
    )
  },
)

CardContent.displayName = "CardContent"

/**
 * Behaves the same way as <CardContent> except that it is scrollable when a height is set.
 * Also becomes scrollable if the Card's height is too small to fit the content.
 * Grows to the maximum available height if it's inside a Card with an explicitly set height.
 */
export const ScrollableCardContent = forwardRef<
  HTMLDivElement,
  CardContentProps
>(({ className, children, ...props }, ref) => {
  return (
    <CardContent
      ref={ref}
      {...props}
      className={`scrollable-card-content ${className ?? ""}`}
    >
      {children}
    </CardContent>
  )
})

ScrollableCardContent.displayName = "ScrollableCardContent"

export const cardRowSizes = ["md", "lg"] as const

export type CardRowSize = (typeof cardRowSizes)[number]

export type CardRowProps = ComponentPropsWithoutRef<"div"> & {
  rowTitle: ReactNode

  rowDescription?: ReactNode

  /**
   * The size of the CardRow dictates font-sizes, and icon sizes.
   *
   * @default "md"
   */
  "data-size"?: CardRowSize

  /**
   * The icon(s) to display in the FeaturedIcon component. Note that this is provided
   * as a prop intentionally to restrict the range of sizes that can be used.
   */
  rowIcons?: (OutlineIcon | Logo)[]

  /**
   * Optionally provide a badge to be shown.
   * This component does not enforce that the size of this badge is appropriate.
   * Please ensure the correct size is used by checking designs.
   */
  rowBadge?: ReactNode

  /**
   * Optionally provide an action that the user can take.
   */
  rowAction?: ReactNode
}

export const CardRow = forwardRef<HTMLDivElement, CardRowProps>(
  (
    {
      rowTitle,
      rowDescription,
      rowIcons,
      rowBadge,
      rowAction,
      className,
      ...props
    },
    ref,
  ) => {
    const size = props["data-size"] ?? "md"
    const titleSize = size === "md" ? "title-sm" : "title-md"
    const descriptionSize = size === "md" ? "body-md" : "body-lg"

    return (
      <div className={`card-row-root ${className ?? ""}`} ref={ref} {...props}>
        <div className="card-row">
          <div className="card-row-div-a">
            {rowIcons && rowIcons.length > 0 && (
              <div
                className="card-row-icons card-row-icons-desktop"
                data-size={size}
              >
                {rowIcons.map((rowIcon, i) => {
                  return (
                    <FeaturedIcon
                      key={rowIcon.name}
                      icon={rowIcon}
                      data-variant="square"
                      data-size={size}
                      style={{
                        zIndex: 1000 - i,
                      }}
                    />
                  )
                })}
              </div>
            )}
            <div className="card-row-div-b">
              <div className="card-row-div-c">
                <div className="card-row-div-d">
                  {rowIcons && rowIcons.length > 0 && (
                    <div
                      className="card-row-icons card-row-icons-mobile"
                      data-size={size}
                    >
                      {rowIcons.map((rowIcon, i) => {
                        return (
                          <FeaturedIcon
                            key={rowIcon.name}
                            icon={rowIcon}
                            data-variant="square"
                            data-size={size}
                            style={{
                              zIndex: 1000 - i,
                            }}
                          />
                        )
                      })}
                    </div>
                  )}
                  <p
                    className={`card-row-title text-color-primary-subdued ${titleSize}`}
                  >
                    {rowTitle}
                  </p>
                </div>
                {rowBadge}
              </div>
              {rowDescription && (
                <p
                  className={`card-row-description text-color-secondary-subdued ${descriptionSize}`}
                >
                  {rowDescription}
                </p>
              )}
            </div>
          </div>
          {rowAction}
        </div>
      </div>
    )
  },
)

CardRow.displayName = "CardRow"
